import {
  ChangeDetectionStrategy,
  Component,
  input,
  output,
} from '@angular/core';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';

export type RvBadgeShape = 'chip' | 'icon' | 'input';
export type RvBadgeAppearance = 'light' | 'solid';
export type RvBadgeSize = 'default' | 'compact';
export type RvBadgeMode = 'display' | 'button';
export type RvBadgeLabelSize = 'default' | 'small';
export type RvBadgeColor =
  | 'green'
  | 'yellow'
  | 'red'
  | 'blue'
  | 'gray'
  | 'purple'
  | 'sky'
  | 'amber'
  | 'orange';

@Component({
  selector: 'rv-badge',
  imports: [MatChipsModule, MatIconModule],
  template: `
    @if (shape() === 'icon' && icon()) {
      <mat-icon [svgIcon]="icon()" class="thin" />
    } @else if (mode() === 'button') {
      <button
        type="button"
        class="rv-badge-surface rv-badge-button"
        [disabled]="disabled() || loading()"
        [attr.aria-label]="ariaLabel() || null"
        [attr.aria-busy]="loading()"
        (click)="onActivate()"
      >
        @if (loading()) {
          <span class="rv-badge-spinner" aria-hidden="true"></span>
          <span class="tw-sr-only">{{ text() }} loading</span>
        } @else {
          @if (imageSrc()) {
            <img
              class="rv-badge-avatar"
              [src]="imageSrc()"
              [alt]="imageAlt()"
            />
          } @else if (avatarText()) {
            <span class="rv-badge-avatar" aria-hidden="true">{{
              avatarText()
            }}</span>
          } @else if (icon()) {
            <mat-icon [svgIcon]="icon()" class="small" />
          } @else if (emoji()) {
            <span class="rv-badge-emoji" aria-hidden="true">{{ emoji() }}</span>
          }
          @if (text()) {
            <span class="rv-badge-span">{{ text() }}</span>
          }
        }
      </button>
    } @else {
      <mat-chip
        class="rv-badge-surface"
        [disabled]="disabled()"
        (removed)="onRemoved()"
      >
        <span class="rv-badge-content">
          @if (imageSrc()) {
            <img
              class="rv-badge-avatar"
              [src]="imageSrc()"
              [alt]="imageAlt()"
            />
          } @else if (avatarText()) {
            <span class="rv-badge-avatar" aria-hidden="true">{{
              avatarText()
            }}</span>
          } @else if (icon()) {
            <mat-icon [svgIcon]="icon()" class="small" />
          } @else if (emoji()) {
            <span class="rv-badge-emoji" aria-hidden="true">{{ emoji() }}</span>
          }
          @if (text()) {
            <span class="rv-badge-span">{{ text() }}</span>
          }
        </span>
        @if (shape() === 'input' && removable()) {
          <button
            type="button"
            matChipRemove
            [attr.aria-label]="removeButtonAriaLabel()"
            (click)="onRemoveClick($event)"
          >
            <mat-icon svgIcon="close" />
          </button>
        }
      </mat-chip>
    }
  `,
  styleUrls: ['./rv-badge.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class]':
      '"rv-badge rv-badge--" + shape() + " rv-badge--" + color() + " rv-badge--" + appearance() + " rv-badge--" + size() + " rv-badge--" + mode() + " rv-badge--label-" + labelSize() + (disabled() ? " rv-badge--disabled" : "") + (hasMedia() ? " rv-badge--has-media" : "")',
    '[attr.aria-label]': 'mode() === "display" ? ariaLabel() || null : null',
    '[attr.aria-disabled]': 'disabled() ? "true" : null',
    '[style.--rv-badge-media-size]': 'mediaSize() + "px"',
    '[style.--rv-badge-avatar-font-size]': 'avatarFontSize()',
    '[style.--rv-badge-avatar-background]': 'avatarBackground()',
  },
})
export class RvBadgeComponent {
  readonly shape = input<RvBadgeShape>('chip');
  readonly mode = input<RvBadgeMode>('display');
  readonly labelSize = input<RvBadgeLabelSize>('default');

  /** Screen reader label when the badge is icon-only (no visible text). */
  readonly ariaLabel = input<string | null>(null);

  readonly color = input<RvBadgeColor>('gray');
  readonly appearance = input<RvBadgeAppearance>('light');
  readonly size = input<RvBadgeSize>('default');

  readonly icon = input<string | null>(null);
  readonly emoji = input<string | null>(null);
  readonly imageSrc = input<string | null>(null);
  readonly imageAlt = input('');
  readonly avatarText = input<string | null>(null);
  readonly avatarBackground = input('#4b5563');
  readonly mediaSize = input(20);
  readonly text = input<string>('');

  readonly removable = input(false);
  readonly disabled = input(false);
  readonly loading = input(false);
  readonly removeAriaLabel = input<string | null>(null);
  readonly removed = output<void>();
  readonly activated = output<void>();

  hasMedia(): boolean {
    return Boolean(
      this.imageSrc() || this.avatarText() || this.icon() || this.emoji(),
    );
  }

  avatarFontSize(): string {
    return `${Math.max(Math.round(this.mediaSize() * 0.4), 10)}px`;
  }

  removeButtonAriaLabel(): string {
    return (
      this.removeAriaLabel() ||
      (this.text() ? `Remove ${this.text()}` : 'Remove pill')
    );
  }

  onRemoveClick(event: MouseEvent): void {
    event.stopPropagation();
  }

  onRemoved(): void {
    if (!this.disabled()) {
      this.removed.emit();
    }
  }

  onActivate(): void {
    if (!this.disabled() && !this.loading()) {
      this.activated.emit();
    }
  }
}
